import type { EnvironmentId, PhaseEvent } from "@cmmd-forge/contracts";

import {
  setWorkspaceReadinessPhase,
  setWorkspaceReadinessPhaseForEnvironment,
} from "~/state/workspaceReadinessAtom";

/**
 * FINDING-CB1: SSE client for `GET /api/project/readiness`.
 *
 * Opens a fetch-backed SSE stream before (or alongside) the WS upgrade.
 * Browsers expose HTTP response bodies here but NOT on failed WS handshakes,
 * and fetch lets paired desktop/saved environments attach bearer auth.
 *
 * The server closes the stream when phase reaches `app-ready` or `error`.
 * After that, post-upgrade phase deltas flow through the Effect RPC
 * subscription on the data WS. Both sources write into the same atom
 * with monotonic-ts merge so the SPA sees a coherent transition across
 * the SSE-to-WS handover.
 */

export interface WorkspaceReadinessClientHandle {
  /** Close the stream and stop receiving phase events. */
  readonly close: () => void;
}

export interface WorkspaceReadinessStreamInput {
  readonly url: string;
  readonly bearerToken?: string;
  readonly environmentId?: EnvironmentId;
}

function applyPhase(event: PhaseEvent, environmentId?: EnvironmentId): void {
  if (environmentId) {
    setWorkspaceReadinessPhaseForEnvironment(environmentId, event);
    return;
  }
  setWorkspaceReadinessPhase(event);
}

function parseSseFrame(frame: string): {
  readonly eventName: string | null;
  readonly data: string;
} {
  let eventName: string | null = null;
  const dataLines: string[] = [];

  for (const line of frame.split(/\r?\n/)) {
    if (line.startsWith("event:")) {
      eventName = line.slice("event:".length).trim();
      continue;
    }
    if (line.startsWith("data:")) {
      dataLines.push(line.slice("data:".length).trimStart());
    }
  }

  return { eventName, data: dataLines.join("\n") };
}

function handleSseFrame(frame: string, environmentId?: EnvironmentId): boolean {
  const parsedFrame = parseSseFrame(frame);
  if (parsedFrame.eventName !== "phase" || !parsedFrame.data) return false;

  try {
    const parsed = JSON.parse(parsedFrame.data) as PhaseEvent;
    applyPhase(parsed, environmentId);
    return parsed.phase === "app-ready" || parsed.phase === "error";
  } catch {
    return false;
  }
}

async function readSseResponse(
  response: Response,
  environmentId: EnvironmentId | undefined,
  signal: AbortSignal,
): Promise<void> {
  if (!response.body) return;

  const reader = response.body.getReader();
  const decoder = new TextDecoder();
  let buffer = "";

  while (!signal.aborted) {
    const { done, value } = await reader.read();
    if (done) break;
    buffer += decoder.decode(value, { stream: true });

    let boundary = buffer.indexOf("\n\n");
    while (boundary !== -1) {
      const frame = buffer.slice(0, boundary);
      buffer = buffer.slice(boundary + 2);
      if (handleSseFrame(frame, environmentId)) {
        return;
      }
      boundary = buffer.indexOf("\n\n");
    }
  }
}

/**
 * Open the workspace readiness SSE stream. Returns a handle whose
 * `close()` aborts the underlying fetch. Idempotent: calling
 * `close()` more than once is a no-op.
 *
 * Failure handling:
 *   - HTTP/network errors are swallowed so a flaky readiness channel
 *     doesn't surface as an overlay error. The readiness atom stays
 *     whatever it last was, and the time-derived fallback continues if
 *     no phase arrives.
 *   - JSON parse failures on a frame are swallowed individually so one
 *     bad frame doesn't poison the stream.
 *   - 404 (old server, no handler): the atom stays null and the SPA uses
 *     the legacy time-derived overlay copy.
 */
export const openWorkspaceReadinessStream = (
  input: WorkspaceReadinessStreamInput | string = "/api/workspace/readiness",
): WorkspaceReadinessClientHandle => {
  if (typeof fetch === "undefined") {
    // SSR / test environments without fetch. No-op handle.
    return { close: () => {} };
  }

  const config = typeof input === "string" ? { url: input } : input;
  let closed = false;
  const abortController = new AbortController();

  void fetch(config.url, {
    credentials: config.bearerToken ? "omit" : "include",
    signal: abortController.signal,
    ...(config.bearerToken ? { headers: { Authorization: `Bearer ${config.bearerToken}` } } : {}),
  })
    .then((response) => {
      if (!response.ok) return;
      return readSseResponse(response, config.environmentId, abortController.signal);
    })
    .catch(() => {
      // Readiness is a progressive hint. Connection failures leave the
      // overlay on its existing time-derived fallback.
    });

  const close = () => {
    if (closed) return;
    closed = true;
    abortController.abort();
  };

  return { close };
};
